import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { formatPrice } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { TrendingUp, ShoppingBag, Users, Package, AlertTriangle, ArrowRight } from "lucide-react";
import { ResponsiveContainer, LineChart, Line, XAxis, YAxis, Tooltip, CartesianGrid } from "recharts";

const isEventPackage = (product) => product.is_event_package === true || (product.is_event_package == null && /event|wedding|corporate|exhibition|catering|farmhouse|mice|conference|launch|promotion|gifting|celebration|venue|production|experience|activation/i.test(`${product.title} ${product.category} ${(product.tags || []).join(" ")}`));

export default function AdminDashboard() {
    const [data, setData] = useState(null);
    const [eventPackages, setEventPackages] = useState([]);
    useEffect(() => {
        let mounted = true;
        Promise.allSettled([api.get("/admin/overview"), api.get("/admin/products")]).then(([overviewResult, productsResult]) => {
            if (!mounted) return;
            if (overviewResult.status === "fulfilled") setData(overviewResult.value.data);
            if (productsResult.status === "fulfilled") setEventPackages((productsResult.value.data || []).filter((product) => !product.demo_catalog && isEventPackage(product)));
        });
        return () => { mounted = false; };
    }, []);
    if (!data) return <p>Loading…</p>;

    const packageSkus = new Set(eventPackages.flatMap((product) => (product.variants || []).map((variant) => variant.sku)));
    const lowStock = (data.low_stock || []).filter((item) => packageSkus.has(item.sku));

    const stats = [
        { label: "Revenue (paid)", value: formatPrice(data.revenue), icon: TrendingUp },
        { label: "Bookings", value: data.total_orders, icon: ShoppingBag, sub: `${data.paid_orders} confirmed · ${data.pending_orders} pending` },
        { label: "Clients", value: data.customers, icon: Users },
        { label: "Event packages", value: eventPackages.length, icon: Package },
    ];

    return (
        <div data-testid="admin-dashboard">
            <h1 className="font-display text-4xl">Dashboard</h1>
            <p className="text-muted-foreground mt-1">Your events, clients and upcoming work at a glance.</p>

            <div className="mt-8 grid grid-cols-2 lg:grid-cols-4 gap-4">
                {stats.map(s => (
                    <Card key={s.label} className="rounded-[20px] border-[#E7DED2] bg-white/80">
                        <CardContent className="p-5">
                            <div className="flex items-center justify-between">
                                <p className="font-mono-ui text-muted-foreground">{s.label}</p>
                                <s.icon className="h-4 w-4 text-[#1B1B1F]" />
                            </div>
                            <p className="font-display text-3xl mt-2">{s.value}</p>
                            {s.sub && <p className="text-xs text-muted-foreground mt-1">{s.sub}</p>}
                        </CardContent>
                    </Card>
                ))}
            </div>

            <div className="mt-8 grid lg:grid-cols-[1.5fr_1fr] gap-6">
                <Card className="rounded-[20px] border-[#E7DED2] bg-white/80">
                    <CardContent className="p-6">
                        <h3 className="font-display text-2xl">Revenue — last 14 days</h3>
                        <div className="h-64 mt-4">
                            <ResponsiveContainer width="100%" height="100%">
                                <LineChart data={data.revenue_chart}>
                                    <CartesianGrid strokeDasharray="3 3" stroke="#E7DED2" />
                                    <XAxis dataKey="date" stroke="#857C72" fontSize={11} />
                                    <YAxis stroke="#857C72" fontSize={11} tickFormatter={(v) => `₹${(v/100).toLocaleString("en-IN")}`} />
                                    <Tooltip formatter={(v) => formatPrice(v)} contentStyle={{ borderRadius: 12, border: "1px solid #E7DED2" }} />
                                    <Line type="monotone" dataKey="revenue" stroke="#121214" strokeWidth={2.5} dot={{ r: 3 }} />
                                </LineChart>
                            </ResponsiveContainer>
                        </div>
                    </CardContent>
                </Card>
                <Card className="rounded-[20px] border-[#E7DED2] bg-white/80">
                    <CardContent className="p-6">
                        <div className="flex items-center justify-between">
                            <h3 className="font-display text-2xl">Availability alerts</h3>
                            <AlertTriangle className="h-4 w-4 text-[#C07A5A]" />
                        </div>
                        {lowStock.length === 0 ? <p className="text-sm text-muted-foreground mt-3">No event-package availability alerts.</p> : (
                            <ul className="mt-4 space-y-2 max-h-64 overflow-y-auto">
                                {lowStock.map(s => (
                                    <li key={s.sku} className="flex justify-between items-center text-sm border-b border-[#E7DED2] pb-2">
                                        <div>
                                            <p className="font-medium">{s.title}</p>
                                            <p className="text-xs text-muted-foreground">{s.color} / {s.size} · {s.sku}</p>
                                        </div>
                                        <span className="text-[#C07A5A] font-medium">{s.stock}</span>
                                    </li>
                                ))}
                            </ul>
                        )}
                        <Link to="/admin/inventory" className="mt-4 inline-flex items-center gap-1 text-xs font-mono-ui">Manage availability <ArrowRight className="h-3.5 w-3.5" /></Link>
                    </CardContent>
                </Card>
            </div>

            <Card className="mt-6 rounded-[20px] border-[#E7DED2] bg-white/80">
                <CardContent className="p-6">
                    <div className="flex items-center justify-between">
                        <h3 className="font-display text-2xl">Recent bookings</h3>
                        <Link to="/admin/orders" className="text-xs font-mono-ui">View bookings</Link>
                    </div>
                    <div className="mt-4 overflow-x-auto">
                        <table className="w-full text-sm">
                            <thead>
                                <tr className="text-left text-xs font-mono-ui text-muted-foreground border-b border-[#E7DED2]">
                                    <th className="py-2">Order</th><th>Customer</th><th>Status</th><th>Total</th><th>Date</th>
                                </tr>
                            </thead>
                            <tbody>
                                {data.recent_orders.map(o => (
                                    <tr key={o.id} className="border-b border-[#F4EFE6]">
                                        <td className="py-3 font-medium">{o.order_number}</td>
                                        <td>{o.customer_name || o.address.full_name}</td>
                                        <td><span className="text-xs font-mono-ui px-2 py-1 rounded-full bg-[#F4EFE6]">{o.status.replace("_", " ")}</span></td>
                                        <td>{formatPrice(o.total)}</td>
                                        <td className="text-muted-foreground">{new Date(o.created_at).toLocaleDateString()}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </CardContent>
            </Card>
        </div>
    );
}
